import type {
  CompressedImage,
  ImageCompressor,
} from '@obraplan/core/application/ports/image-compressor';

/**
 * Compressão pelo `canvas` do próprio navegador.
 *
 * Sem dependência externa: `createImageBitmap` decodifica, o canvas redimensiona e
 * `toBlob` reencoda em JPEG. Uma biblioteca de imagem custaria centenas de kilobytes no
 * pacote para fazer o que três APIs nativas já fazem.
 *
 * **Nada aqui lança.** Toda falha vira `undefined`, e quem chama guarda o arquivo
 * original: uma foto sem compressão ocupa mais espaço, uma foto perdida custa a evidência
 * do dia. É por isso que o formato não reconhecido, o navegador sem suporte e o canvas que
 * recusou a imagem terminam todos no mesmo lugar.
 */
export class CanvasImageCompressor implements ImageCompressor {
  async compress(
    bytes: Uint8Array,
    mimeType: string,
    options: { maxDimension: number; quality: number },
  ): Promise<CompressedImage | undefined> {
    // PDF não se redimensiona, e o que não é imagem não passa por aqui.
    if (!mimeType.startsWith('image/')) return undefined;
    if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
      return undefined;
    }

    let bitmap: ImageBitmap | undefined;
    try {
      const source = new Blob([bytes as unknown as BlobPart], { type: mimeType });
      bitmap = await createImageBitmap(source);

      const scale = Math.min(1, options.maxDimension / Math.max(bitmap.width, bitmap.height));
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (context === null) return undefined;
      context.drawImage(bitmap, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', options.quality);
      });
      if (blob === null) return undefined;

      const compressed = new Uint8Array(await blob.arrayBuffer());

      // Reencodar nem sempre encolhe: uma imagem já otimizada, ou um PNG pequeno de
      // desenho, pode sair maior em JPEG. Nesse caso o original vence — comprimir não
      // pode ser uma forma de gastar mais cota.
      if (compressed.byteLength >= bytes.byteLength && scale === 1) return undefined;

      return { bytes: compressed, mimeType: 'image/jpeg', width, height };
    } catch {
      return undefined;
    } finally {
      bitmap?.close();
    }
  }
}
